import React, { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence, useInView, useMotionValue, useTransform, animate } from 'framer-motion';
import { MessageCircle, CreditCard, ShieldCheck, Star, CheckCircle2, ChevronDown, Play, BadgeCheck, Zap, TrendingUp, DollarSign, Clock, Users } from 'lucide-react';

// ══════════════════════════════════════════════════════════════════
//  CONFIGURACIÓN
// ══════════════════════════════════════════════════════════════════
const PHONE = '584123555747';
const WA_MSG = encodeURIComponent('Hola, quiero el Masterkit Elite por $19 a Tasa BCV. ¿Cómo pago con Pago Móvil?');
const WA_URL = `https://wa.me/${PHONE}?text=${WA_MSG}`;
const CHECKOUT = 'https://checkout.digitalsolvex.shop/cart/44388729454680:1';
const YT_ID = 'dSrultJNhdQ';
const YT_THUMB = `https://img.youtube.com/vi/${YT_ID}/hqdefault.jpg`;
const YT_URL = `https://youtu.be/${YT_ID}`;
const PIXEL_ID = '951909037158376';

// ══════════════════════════════════════════════════════════════════
//  CSS KEYFRAMES GLOBALES
// ══════════════════════════════════════════════════════════════════
function GlobalStyles() {
  return <style data-id="element-0" data-mp="3mjztd6hzp62c">{`
      @keyframes blob {
        0%,100% { border-radius:60% 40% 30% 70%/60% 30% 70% 40%; transform:translate(0,0) scale(1); }
        25%      { border-radius:30% 60% 70% 40%/50% 60% 30% 60%; transform:translate(-25px,20px) scale(1.05); }
        50%      { border-radius:50% 60% 30% 60%/30% 50% 60% 40%; transform:translate(20px,-15px) scale(0.95); }
        75%      { border-radius:40% 60% 60% 30%/60% 40% 50% 40%; transform:translate(-10px,25px) scale(1.02); }
      }
      @keyframes shimmer {
        0%   { transform:translateX(-150%); }
        100% { transform:translateX(250%); }
      }
      @keyframes float {
        0%,100% { transform:translateY(0px); }
        50%     { transform:translateY(-12px); }
      }
      @keyframes gradient-x {
        0%,100% { background-position:0% 50%; }
        50%     { background-position:100% 50%; }
      }
      @keyframes ping-ring {
        0%   { transform:scale(1); opacity:.6; }
        100% { transform:scale(2.2); opacity:0; }
      }
      @keyframes grid-pulse {
        0%,100% { opacity:.03; }
        50%     { opacity:.06; }
      }
      .blob-1 { animation: blob 9s ease-in-out infinite; }
      .blob-2 { animation: blob 11s ease-in-out infinite 2s; }
      .blob-3 { animation: blob 13s ease-in-out infinite 4.5s; }
      .float-anim { animation: float 4s ease-in-out infinite; }
      .float-slow  { animation: float 6s ease-in-out infinite 1s; }
      .gradient-animate {
        background-size: 200% 200%;
        animation: gradient-x 3.5s ease infinite;
      }
      .shimmer-wrap { position:relative; overflow:hidden; }
      .shimmer-wrap::after {
        content:'';
        position:absolute;
        top:0; left:0;
        width:50%; height:100%;
        background:linear-gradient(90deg,transparent,rgba(255,255,255,.18),transparent);
        animation:shimmer 2.8s ease-in-out infinite;
        pointer-events:none;
      }
      .grid-bg {
        background-image:
          linear-gradient(rgba(255,255,255,.04) 1px,transparent 1px),
          linear-gradient(90deg,rgba(255,255,255,.04) 1px,transparent 1px);
        background-size:48px 48px;
        animation: grid-pulse 6s ease-in-out infinite;
      }
      .ring-ping {
        position:absolute; inset:-4px;
        border-radius:inherit;
        border:2px solid #25D366;
        animation: ping-ring 2s ease-out infinite;
      }
      .ring-ping-2 {
        position:absolute; inset:-4px;
        border-radius:inherit;
        border:2px solid #25D366;
        animation: ping-ring 2s ease-out infinite .7s;
      }
    `}</style>;
}

// ══════════════════════════════════════════════════════════════════
//  COMPONENTES DE UTILIDAD
// ══════════════════════════════════════════════════════════════════

/** Número animado que cuenta desde 0 cuando entra en pantalla */
function CountUp({
  to,
  suffix = ''
}: {
  to: number;
  suffix?: string;
}) {
  const ref = useRef(null);
  const inView = useInView(ref, {
    once: true
  });
  const count = useMotionValue(0);
  const rounded = useTransform(count, v => Math.round(v));
  const [display, setDisplay] = useState(0);
  useEffect(() => {
    const unsub = rounded.on('change', v => setDisplay(v));
    return unsub;
  }, [rounded]);
  useEffect(() => {
    if (inView) animate(count, to, {
      duration: 1.8,
      ease: 'easeOut'
    });
  }, [inView, count, to]);
  return <span ref={ref} data-id="element-1" data-mp="14wetsqber4ar">{display}{suffix}</span>;
}

/** Botón WhatsApp con shimmer + anillos de ping */
function BtnWhatsApp({
  label = 'Comprar por WhatsApp — $19 a Tasa BCV',
  size = 'lg'
}: {
  label?: string;
  size?: 'lg' | 'sm';
}) {
  const lg = 'py-4 px-8 text-lg font-black';
  const sm = 'py-3 px-6 text-sm font-bold';
  return <motion.a href={WA_URL} target="_blank" rel="noopener noreferrer" whileHover={{
    scale: 1.03,
    boxShadow: '0 0 48px rgba(37,211,102,.65)'
  }} whileTap={{
    scale: 0.97
  }} onClick={() => (window as any).fbq?.('track', 'Contact')} className={`shimmer-wrap relative flex items-center justify-center gap-2.5 rounded-xl w-full
        bg-[#25D366] hover:bg-[#1db954] text-white transition-colors duration-200
        shadow-[0_0_30px_rgba(37,211,102,0.45)] ${size === 'lg' ? lg : sm}`} style={{
    isolation: 'isolate'
  }} data-id="element-2" data-mp="3koubf1ihda52">
      <span className="ring-ping" data-id="element-3" data-mp="2ev6iw6s2by9w" />
      <span className="ring-ping-2" data-id="element-4" data-mp="2eu4vr3xrvlbt" />
      <MessageCircle className="w-5 h-5 flex-shrink-0 relative z-10" data-id="element-5" data-mp="2ex0xl3cqqu5j" />
      <span className="relative z-10" data-id="element-6" data-mp="2jsg57dk5ntti">{label}</span>
    </motion.a>;
}

/** Botón checkout secundario */
function BtnCheckout() {
  return <motion.a href={CHECKOUT} target="_blank" rel="noopener noreferrer" whileHover={{
    scale: 1.02,
    borderColor: 'rgba(255,255,255,.3)'
  }} whileTap={{
    scale: 0.98
  }} onClick={() => (window as any).fbq?.('track', 'InitiateCheckout')} className="flex items-center justify-center gap-2 w-full py-3 px-6 text-sm font-semibold
        text-gray-400 hover:text-white border border-white/10 rounded-xl transition-colors duration-200" data-id="element-7" data-mp="2supdrxvv5d49">
      <CreditCard className="w-4 h-4" data-id="element-8" data-mp="1wu27o02ebt26" />
      Pagar con Tarjeta o Binance
    </motion.a>;
}

/** Thumbnail del video — abre YouTube en nueva pestaña */
function VideoThumb() {
  return <motion.a href={YT_URL} target="_blank" rel="noopener noreferrer" whileHover={{
    scale: 1.02
  }} className="relative w-full aspect-video rounded-2xl overflow-hidden group cursor-pointer bg-[#0a0a0a] block" data-id="element-9" data-mp="25se109und4yb">
      <img src={YT_THUMB} alt="Demo Masterkit Elite" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" data-id="element-10" data-mp="2fpis8o3cnqlz" />
      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent group-hover:from-black/40 transition-all" data-id="element-11" data-mp="2pi7pof22f3yb" />
      <div className="absolute inset-0 flex items-center justify-center" data-id="element-12" data-mp="33etnxr5ueut9">
        <motion.div whileHover={{
        scale: 1.12
      }} className="w-18 h-18 w-[72px] h-[72px] bg-white/95 rounded-full flex items-center justify-center shadow-2xl" data-id="element-13" data-mp="2yje9ax00guvs">
          <Play className="w-7 h-7 text-black ml-1.5" fill="black" data-id="element-14" data-mp="3n1eopkheqlbi" />
        </motion.div>
      </div>
      <div className="absolute bottom-4 inset-x-0 text-center" data-id="element-15" data-mp="384nkmxwsglfk">
        <span className="text-xs text-white/90 bg-black/55 px-4 py-1.5 rounded-full backdrop-blur-sm" data-id="element-16" data-mp="3d4a2jknkapup">
          ▶ Ver cómo funciona la Calculadora Anti-inflación
        </span>
      </div>
    </motion.a>;
}

/** FAQ accordion */
function FAQ() {
  const [open, setOpen] = useState<number | null>(0);
  const items = [{
    q: '¿Cómo pago con Pago Móvil?',
    a: 'Haz clic en el botón verde de WhatsApp y te envío los datos de Pago Móvil al instante. Confirmo tu pago y recibes el Masterkit en menos de 5 minutos. También acepto Binance Pay y tarjetas internacionales.'
  }, {
    q: '¿Funciona si no me llevo bien con la tecnología?',
    a: 'Sí. Está diseñado para ser tan fácil como anotar en un cuaderno. Incluye manual paso a paso y soporte directo por WhatsApp. Si te trancas en algo, te ayudo hasta que lo domines.'
  }, {
    q: '¿Tengo que pagar mensualidades o por cada computadora?',
    a: 'No. Un solo pago de $19 y puedes copiarlo en todas las computadoras que necesites — tus cajas de cobro, la laptop de la casa. Sin licencias, sin mensualidades. El archivo es tuyo de por vida.'
  }];
  return <div className="space-y-3" data-id="element-17" data-mp="2hwrlb1be5k3u">
      {items.map((item, i) => <motion.div key={i} initial={{
      opacity: 0,
      y: 12
    }} whileInView={{
      opacity: 1,
      y: 0
    }} viewport={{
      once: true
    }} transition={{
      delay: i * 0.08
    }} className={`border rounded-2xl overflow-hidden transition-all duration-300
            ${open === i ? 'border-[#25D366]/40 bg-[#25D366]/5' : 'border-white/8 bg-[#0a0a0a] hover:border-white/15'}`} data-id="element-18" data-mp="2d5t0ppjddzus" data-mpi={i}>
          <button onClick={() => setOpen(open === i ? null : i)} className="w-full flex items-center justify-between p-5 text-left gap-4" data-id="element-19" data-mp="1y7qxxhk5xiel">
            <span className={`font-bold text-base transition-colors ${open === i ? 'text-[#25D366]' : 'text-white'}`} data-id="element-20" data-mp="9po0qwqi8u05">
              {item.q}
            </span>
            <motion.div animate={{
          rotate: open === i ? 180 : 0
        }} transition={{
          duration: 0.25
        }} data-id="element-21" data-mp="dscbxb2oyd0a">
              <ChevronDown className="w-4 h-4 text-gray-500 flex-shrink-0" data-id="element-22" data-mp="inro7yl1w0ul" />
            </motion.div>
          </button>
          <AnimatePresence initial={false} data-id="element-23" data-mp="3m6kx3y2vw1if">
            {open === i && <motion.div initial={{
          height: 0,
          opacity: 0
        }} animate={{
          height: 'auto',
          opacity: 1
        }} exit={{
          height: 0,
          opacity: 0
        }} transition={{
          duration: 0.28
        }} data-id="element-24" data-mp="3m4gh4hrpnc1s">
                <p className="px-5 pb-5 text-gray-400 text-sm leading-relaxed border-t border-white/8 pt-3" data-id="element-25" data-mp="3w2e5d59kl8ue">{item.a}</p>
              </motion.div>}
          </AnimatePresence>
        </motion.div>)}
    </div>;
}

// ══════════════════════════════════════════════════════════════════
//  META PIXEL
// ══════════════════════════════════════════════════════════════════
function initPixel() {
  if (typeof window === 'undefined') return;
  const w = window as any;
  if (w.fbq) return;
  const n = w.fbq = function (...a: any[]) {
    n.callMethod ? n.callMethod(...a) : n.queue.push(a);
  };
  w._fbq = n;
  n.push = n;
  n.loaded = true;
  n.version = '2.0';
  n.queue = [];
  const s = document.createElement('script');
  s.async = true;
  s.src = 'https://connect.facebook.net/en_US/fbevents.js';
  document.head.appendChild(s);
  w.fbq('init', PIXEL_ID);
  w.fbq('track', 'PageView');
}

// ══════════════════════════════════════════════════════════════════
//  APP
// ══════════════════════════════════════════════════════════════════
const stagger = {
  hidden: {},
  show: {
    transition: {
      staggerChildren: 0.08
    }
  }
};
const fadeUp = {
  hidden: {
    opacity: 0,
    y: 22
  },
  show: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5
    }
  }
};
export default function App() {
  useEffect(() => {
    initPixel();
  }, []);
  const beneficios = ['Calculadora Anti-inflación — actualizas la tasa BCV y todos tus precios se ajustan solos', 'Control de Inventario — registras facturas de proveedor y el stock se suma automáticamente', 'Estructura de Costos — sabe exactamente cuánto te costó cada producto y cuánto cobrar', 'Registro de Ventas — cada venta descuenta el stock en tiempo real', 'Cierre de Caja en 2 minutos — cuánto vendiste en $ y Bs, y tu ganancia real del día', 'Dashboard con KPIs — tus 5 productos más vendidos y punto de equilibrio automático', 'Manual de usuario paso a paso + Soporte por WhatsApp incluido'];
  const testimonios = [{
    n: 'Roberto C.',
    r: 'Repuestos Automotrices',
    i: 'RC',
    c: 'from-cyan-500 to-blue-600',
    t: 'Controlar el inventario era un caos. Ahora sé exactamente qué tengo y qué cobrar. Las facturas se suman solas. Vale cada centavo.'
  }, {
    n: 'Andrea P.',
    r: 'Minimarket (3 Cajas)',
    i: 'AP',
    c: 'from-emerald-400 to-green-600',
    t: 'Lo instalé en mis 3 computadoras y funciona perfecto con la tiquetera. Los tickets los mando por WhatsApp. Súper profesional.'
  }, {
    n: 'Carlos V.',
    r: 'Bodega y Purificadora',
    i: 'CV',
    c: 'from-pink-500 to-rose-600',
    t: 'Le tenía miedo a la tecnología pero los tutoriales son claros. Me dieron soporte por WhatsApp. Ya sé cuánto gano cada día exacto.'
  }];
  return <div className="min-h-screen bg-[#060608] text-white font-sans overflow-x-hidden selection:bg-[#25D366]/30" data-id="element-26" data-mp="2ap4qek5fwm5v">
      <GlobalStyles data-id="element-27" data-mp="2yeh6m3z6puho" />

      {/* ══ HERO ════════════════════════════════════════════════════ */}
      <section className="relative pt-16 pb-12 md:pt-24 md:pb-20 px-4 md:px-8 overflow-hidden" data-id="element-28" data-mp="33citz13n8fyy">

        {/* Fondo: grid sutil */}
        <div className="grid-bg absolute inset-0 -z-20 pointer-events-none" data-id="element-29" data-mp="2tn7m1hshthcn" />

        {/* Blobs animados */}
        <div className="blob-1 absolute top-[-8%] left-[-8%] w-[480px] h-[480px] bg-[#25D366]/12 -z-10 pointer-events-none" data-id="element-30" data-mp="3hd1xbux5yxus" />
        <div className="blob-2 absolute bottom-[-5%] right-[-5%] w-[380px] h-[380px] bg-cyan-500/10 -z-10 pointer-events-none" data-id="element-31" data-mp="3m3s5ffwz5et6" />
        <div className="blob-3 absolute top-[35%] right-[15%] w-[260px] h-[260px] bg-amber-500/6 -z-10 pointer-events-none" data-id="element-32" data-mp="1amyiene2wtqx" />

        <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center" data-id="element-33" data-mp="15nlxi86rpwqz">

          {/* Columna texto */}
          <motion.div initial="hidden" animate="show" variants={stagger} data-id="element-34" data-mp="1feztw73fagfw">

            {/* Badge animado */}
            <motion.div variants={fadeUp} className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full
                bg-[#25D366]/12 border border-[#25D366]/25 text-[#25D366] text-xs font-bold mb-6 uppercase tracking-widest" data-id="element-35" data-mp="rqqfvrcgawzb">
              <span className="relative flex h-2 w-2" data-id="element-36" data-mp="11lid2u24fhtk">
                <span className="animate-ping absolute inset-0 rounded-full bg-[#25D366] opacity-70" data-id="element-37" data-mp="wnyy1rxln10k" />
                <span className="relative rounded-full h-2 w-2 bg-[#25D366]" data-id="element-38" data-mp="2d1p619rua9fh" />
              </span>
              Calculadora Anti-inflación · Masterkit Elite
            </motion.div>

            {/* H1 con gradiente animado */}
            <motion.h1 variants={fadeUp} className="font-black text-4xl md:text-5xl lg:text-[3.1rem] leading-[1.1] tracking-tight mb-5" style={{
            fontFamily: "'Poppins',sans-serif"
          }} data-id="element-39" data-mp="howbeglp5vpc">
              Si cobras en bolívares sin saber tu margen en dólares,{' '}
              <span className="text-transparent bg-clip-text gradient-animate" style={{
              backgroundImage: 'linear-gradient(90deg,#f87171,#fb923c,#fbbf24,#f87171)',
              backgroundSize: '200%'
            }} data-id="element-40" data-mp="vnw542o2h0sd">
                estás perdiendo plata todos los días.
              </span>
            </motion.h1>

            <motion.p variants={fadeUp} className="text-gray-300 text-lg leading-relaxed mb-8 max-w-lg font-light" data-id="element-41" data-mp="1aduzduci3s2k">
              El <strong className="text-white font-semibold" data-id="element-42" data-mp="1jcfc8evdr7iv">Masterkit Elite</strong> calcula exactamente cuánto cobrar según la tasa BCV del día. Inventario, costos, fiados y cierre de caja — para Minimarkets, Bodegas, Repuestos y Licorerías.
            </motion.p>

            {/* Precio flotante */}
            <motion.div variants={fadeUp} className="flex items-baseline gap-3 mb-7" data-id="element-43" data-mp="1tgdjrd8xajye">
              <motion.div animate={{
              y: [0, -8, 0]
            }} transition={{
              duration: 3.5,
              repeat: Infinity,
              ease: 'easeInOut'
            }} className="relative" data-id="element-44" data-mp="1oh10f2h9fv0k">
                <div className="absolute -inset-3 bg-[#25D366]/15 rounded-2xl blur-xl" data-id="element-45" data-mp="2h5ewvdspaja0" />
                <span className="relative text-5xl font-black text-white" style={{
                fontFamily: "'Poppins',sans-serif",
                textShadow: '0 0 30px rgba(37,211,102,.4)'
              }} data-id="element-46" data-mp="2c6lhtgxrmk7q">$19</span>
              </motion.div>
              <div data-id="element-47" data-mp="23e9sjyee12tc">
                <p className="text-[#25D366] font-bold text-sm" data-id="element-48" data-mp="23etdzp38y9c4">Pago único · Sin mensualidades</p>
                <p className="text-amber-400 text-xs font-semibold" data-id="element-49" data-mp="2zwpnruyfew1w">Precio en Bs a Tasa BCV oficial del día</p>
                <p className="text-gray-500 text-xs" data-id="element-50" data-mp="2v57iiupde9b4">Entrega en menos de 5 minutos</p>
              </div>
            </motion.div>

            {/* CTAs */}
            <motion.div variants={fadeUp} className="flex flex-col gap-3 max-w-md mb-7" data-id="element-51" data-mp="ta9tex2t640m">
              <BtnWhatsApp data-id="element-52" data-mp="y936ioe6nt09" />
              <BtnCheckout data-id="element-53" data-mp="jidsle9c7zsp" />
            </motion.div>

            {/* Social proof pills */}
            <motion.div variants={fadeUp} className="flex flex-wrap gap-x-5 gap-y-2" data-id="element-54" data-mp="oh7636omjqsv">
              {[{
              icon: ShieldCheck,
              c: 'text-[#25D366]',
              t: 'Pago único de por vida'
            }, {
              icon: Star,
              c: 'text-yellow-400',
              t: '4.9/5 valoración'
            }, {
              icon: Zap,
              c: 'text-cyan-400',
              t: 'Entrega en < 5 min'
            }, {
              icon: Users,
              c: 'text-purple-400',
              t: '87+ negocios activos'
            }].map(({
              icon: Icon,
              c,
              t
            }, i) => <div key={i} className="flex items-center gap-1.5 text-xs text-gray-500" data-id="element-55" data-mp="1ux9i5v9ivlo1" data-mpi={i}>
                  <Icon className={`w-3.5 h-3.5 ${c}`} data-id="element-56" data-mp="1uvgcojanb7hm" />
                  {t}
                </div>)}
            </motion.div>
          </motion.div>

          {/* Video con borde animado */}
          <motion.div initial={{
          opacity: 0,
          x: 40
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          duration: 0.7,
          delay: 0.2
        }} className="relative float-slow" data-id="element-57" data-mp="3ffbp7y6pyafl">
            <div className="absolute -inset-1 bg-gradient-to-tr from-[#25D366]/40 via-cyan-500/20 to-amber-500/20 rounded-3xl blur-xl opacity-60" data-id="element-58" data-mp="3pcziejoof9vn" />
            <div className="relative border border-white/10 rounded-3xl overflow-hidden p-2 bg-[#0d0d0d]
              shadow-[0_0_60px_rgba(37,211,102,.15),0_0_120px_rgba(34,211,238,.08)]" data-id="element-59" data-mp="6xp03yi7s4nw">
              <VideoThumb data-id="element-60" data-mp="gx64mclqccia" />
            </div>
          </motion.div>
        </div>
      </section>

      {/* ══ STATS BAR ═══════════════════════════════════════════════ */}
      <section className="py-8 border-y border-white/6 bg-[#080808] relative overflow-hidden" data-id="element-61" data-mp="2io9hz9n0856j">
        <div className="absolute inset-0 bg-gradient-to-r from-[#25D366]/5 via-transparent to-cyan-500/5 pointer-events-none" data-id="element-62" data-mp="2dqrhm7n1yya5" />
        <div className="max-w-5xl mx-auto px-4 grid grid-cols-2 md:grid-cols-4 gap-6 text-center relative z-10" data-id="element-63" data-mp="1rw99bhnpxwfl">
          {[{
          icon: Users,
          val: 87,
          suf: '+',
          label: 'Negocios activos'
        }, {
          icon: Star,
          val: 49,
          suf: '/5',
          label: 'Valoración'
        }, {
          icon: DollarSign,
          val: 0,
          suf: ' mensualidades',
          label: 'Costo recurrente'
        }, {
          icon: Clock,
          val: 5,
          suf: ' min',
          label: 'Tiempo de entrega'
        }].map(({
          icon: Icon,
          val,
          suf,
          label
        }, i) => <motion.div key={i} initial={{
          opacity: 0,
          y: 16
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: i * 0.1
        }} whileHover={{
          scale: 1.05
        }} className="flex flex-col items-center gap-1 p-3 rounded-2xl hover:bg-white/5 transition-colors cursor-default" data-id="element-64" data-mp="1dz3vgvhrjt9s" data-mpi={i}>
              <Icon className="w-5 h-5 text-[#25D366] mb-1" data-id="element-65" data-mp="2z9kshq5sd7m9" />
              <span className="text-2xl font-black text-white" style={{
            fontFamily: "'Poppins',sans-serif"
          }} data-id="element-66" data-mp="nn0tbzimh9al">
                {val === 49 ? <><CountUp to={4} data-id="element-67" data-mp="ioyzcjjnrpkc" /><span className="text-lg" data-id="element-68" data-mp="iofqdsbzkbig">.9⭐</span></> : <><CountUp to={val} data-id="element-69" data-mp="dwnwp8xj9po3" /><span className="text-sm font-normal text-gray-400" data-id="element-70" data-mp="dw5w603u8pad">{suf}</span></>}
              </span>
              <span className="text-gray-500 text-xs uppercase tracking-wide" data-id="element-71" data-mp="8xl1shb9oztu">{label}</span>
            </motion.div>)}
        </div>
      </section>

      {/* ══ DOLOR ════════════════════════════════════════════════════ */}
      <section className="py-16 md:py-20 px-4 md:px-8 relative overflow-hidden" data-id="element-72" data-mp="3r3pqiuwbf6nl">
        <div className="blob-3 absolute top-0 right-0 w-[350px] h-[350px] bg-rose-500/6 pointer-events-none -z-10" data-id="element-73" data-mp="3r5tsh7anlvl8" />
        <div className="max-w-4xl mx-auto text-center mb-10" data-id="element-74" data-mp="1pccl84qfqwr0">
          <motion.div initial={{
          opacity: 0,
          scale: .9
        }} whileInView={{
          opacity: 1,
          scale: 1
        }} viewport={{
          once: true
        }} data-id="element-75" data-mp="1pbuj4rh3dob6">
            <span className="inline-flex items-center px-3 py-1.5 rounded-full bg-rose-500/10 border border-rose-500/15 text-rose-400 text-xs font-bold uppercase tracking-widest mb-5" data-id="element-76" data-mp="1kb6v8eo5tnl6">
              🚨 La realidad del comerciante venezolano
            </span>
            <h2 className="text-2xl md:text-3xl font-black text-white leading-tight" style={{
            fontFamily: "'Poppins',sans-serif"
          }} data-id="element-77" data-mp="22ghnamxvw4eg">
              Si esto te pasa a diario,{' '}
              <span className="text-rose-400" data-id="element-78" data-mp="1the3thut1mzf">el negocio está perdiendo dinero</span>
            </h2>
          </motion.div>
        </div>

        <div className="max-w-4xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-5" data-id="element-79" data-mp="1og6uvfgxehr6">
          {[{
          e: '📉',
          t: 'Pierdes plata con la tasa',
          d: 'No tienes tus costos en dólares claros. Cuando el BCV sube, cobras de menos sin darte cuenta.'
        }, {
          e: '📦',
          t: 'No sabes cuánto tienes',
          d: 'Tu inventario vive en tu cabeza o en un cuaderno. Compras de más o te quedas sin lo que más vende.'
        }, {
          e: '🧮',
          t: 'Calculas todo a mano',
          d: 'Sacas la calculadora cada vez que entra un cliente. Lento, impreciso y con riesgo de error.'
        }].map(({
          e,
          t,
          d
        }, i) => <motion.div key={i} initial={{
          opacity: 0,
          y: 24
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: i * 0.12
        }} whileHover={{
          y: -6,
          boxShadow: '0 20px 40px rgba(248,113,113,.12)'
        }} className="group bg-[#0a0a0a] border border-white/6 rounded-2xl p-6 cursor-default
                hover:border-rose-500/30 transition-all duration-300 relative overflow-hidden" data-id="element-80" data-mp="3lenxenbxsgqp" data-mpi={i}>
              <div className="absolute inset-0 bg-gradient-to-br from-rose-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-400 pointer-events-none" data-id="element-81" data-mp="37g6d6zl8pmdj" />
              <div className="text-4xl mb-4 group-hover:scale-110 transition-transform duration-300 inline-block" data-id="element-82" data-mp="mqgx6j8svuj9">{e}</div>
              <h3 className="text-white font-bold text-lg mb-2" data-id="element-83" data-mp="ro8rtl0txur5">{t}</h3>
              <p className="text-gray-400 text-sm leading-relaxed" data-id="element-84" data-mp="20wbn3fbm1lh">{d}</p>
            </motion.div>)}
        </div>
      </section>

      {/* ══ QUÉ INCLUYE ══════════════════════════════════════════════ */}
      <section className="py-16 md:py-20 px-4 md:px-8 bg-[#050507] border-y border-white/5 relative overflow-hidden" data-id="element-85" data-mp="bzltwxb4po29">
        <div className="blob-1 absolute -bottom-20 -left-20 w-[300px] h-[300px] bg-[#25D366]/8 pointer-events-none -z-10" data-id="element-86" data-mp="71kaucdpdoqs" />
        <div className="max-w-3xl mx-auto" data-id="element-87" data-mp="3fgc31l20ch86">
          <motion.div initial={{
          opacity: 0,
          y: 16
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-center mb-10" data-id="element-88" data-mp="3ahhmkprij4r8">
            <h2 className="text-2xl md:text-3xl font-black text-white mb-3" style={{
            fontFamily: "'Poppins',sans-serif"
          }} data-id="element-89" data-mp="3p76s0x6n1uxb">
              Todo lo que incluye el{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#25D366] to-cyan-400" data-id="element-90" data-mp="2wojbzw6i1633">
                Masterkit Elite Básico
              </span>
            </h2>
            <p className="text-gray-500 text-sm" data-id="element-91" data-mp="177xlbzi6lhmc">Un solo archivo Excel · Sin instalaciones · Sin internet constante</p>
          </motion.div>

          <motion.div variants={stagger} initial="hidden" whileInView="show" viewport={{
          once: true
        }} className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-10" data-id="element-92" data-mp="1c0gvsnlknwmx">
            {beneficios.map((b, i) => <motion.div key={i} variants={fadeUp} whileHover={{
            x: 4,
            backgroundColor: 'rgba(37,211,102,.06)',
            borderColor: 'rgba(37,211,102,.2)'
          }} className="flex items-start gap-3 bg-[#0a0a0a] border border-white/6 rounded-xl p-4 transition-all duration-200 cursor-default" data-id="element-93" data-mp="1279vhh5ov6tp" data-mpi={i}>
                <motion.div initial={{
              scale: 0
            }} whileInView={{
              scale: 1
            }} viewport={{
              once: true
            }} transition={{
              delay: i * 0.07,
              type: 'spring',
              stiffness: 200
            }} data-id="element-94" data-mp="1h0lgyqxfdfjh">
                  <CheckCircle2 className="w-4 h-4 text-[#25D366] flex-shrink-0 mt-0.5" data-id="element-95" data-mp="5l6hnghqubl7" />
                </motion.div>
                <span className="text-gray-300 text-sm leading-snug" data-id="element-96" data-mp="prtjze4mfwyb">{b}</span>
              </motion.div>)}
          </motion.div>

          <div className="flex flex-col gap-3 max-w-md mx-auto" data-id="element-97" data-mp="l0235vb9p5ld">
            <BtnWhatsApp label="Quiero el Masterkit por $19 a Tasa BCV →" data-id="element-98" data-mp="g1qtplovwx3o" />
            <BtnCheckout data-id="element-99" data-mp="fzxidt3btopc" />
          </div>
        </div>
      </section>

      {/* ══ TESTIMONIOS ══════════════════════════════════════════════ */}
      <section className="py-16 md:py-20 px-4 md:px-8 relative overflow-hidden" data-id="element-100" data-mp="18qxt0mw5xqpz">
        <div className="blob-2 absolute top-0 left-1/2 w-[400px] h-[400px] bg-cyan-500/6 pointer-events-none -z-10 -translate-x-1/2" data-id="element-101" data-mp="13r30yg0niv3c" />
        <div className="max-w-5xl mx-auto" data-id="element-102" data-mp="yr6tu7wnqnjk">
          <motion.h2 initial={{
          opacity: 0,
          y: 16
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-2xl md:text-3xl font-black text-white text-center mb-10" style={{
          fontFamily: "'Poppins',sans-serif"
        }} data-id="element-103" data-mp="uqws57fwejai">
            Negocios venezolanos que ya{' '}
            <span className="text-[#25D366]" data-id="element-104" data-mp="3fji3d4cswlpj">tomaron el control</span>
          </motion.h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-105" data-mp="1ux07ytodzgu1">
            {testimonios.map((t, i) => <motion.div key={i} initial={{
            opacity: 0,
            y: 24
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: i * 0.13
          }} whileHover={{
            y: -8,
            boxShadow: '0 24px 48px rgba(37,211,102,.1)'
          }} className="bg-[#0a0a0a] border border-white/6 rounded-2xl p-6 hover:border-[#25D366]/25 transition-all duration-300 cursor-default" data-id="element-106" data-mp="1yybyd1f3463a" data-mpi={i}>
                <div className="flex gap-0.5 mb-4" data-id="element-107" data-mp="2mqirzstfdyz0">
                  {[...Array(5)].map((_, j) => <motion.div key={j} initial={{
                opacity: 0,
                scale: 0
              }} whileInView={{
                opacity: 1,
                scale: 1
              }} viewport={{
                once: true
              }} transition={{
                delay: i * 0.1 + j * 0.06,
                type: 'spring'
              }} data-id="element-108" data-mp="2rpvoz6e0gmfb" data-mpi={j}>
                      <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" data-id="element-109" data-mp="xajczv34lfbg" />
                    </motion.div>)}
                </div>
                <p className="text-gray-300 text-sm leading-relaxed mb-5" data-id="element-110" data-mp="20o374q1a5j4q">"{t.t}"</p>
                <div className="flex items-center gap-3 border-t border-white/6 pt-4" data-id="element-111" data-mp="1vozzqyxtv0tl">
                  <div className={`w-10 h-10 rounded-full bg-gradient-to-br ${t.c} flex items-center justify-center flex-shrink-0 shadow-lg`} data-id="element-112" data-mp="29ohsfpe8uy88">
                    <span className="text-xs font-bold text-white" data-id="element-113" data-mp="2jkumiodpbe3i">{t.i}</span>
                  </div>
                  <div data-id="element-114" data-mp="2em9jyiaz8ibw">
                    <div className="flex items-center gap-1" data-id="element-115" data-mp="2emt0pvow50oc">
                      <p className="text-white font-bold text-sm" data-id="element-116" data-mp="2sdeo9xpahysk">{t.n}</p>
                      <BadgeCheck className="w-3.5 h-3.5 text-cyan-400" data-id="element-117" data-mp="2scmw6wuwij71" />
                    </div>
                    <p className="text-gray-500 text-xs" data-id="element-118" data-mp="2ocmbwvl4fds2">{t.r}</p>
                  </div>
                </div>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* ══ FAQ ══════════════════════════════════════════════════════ */}
      <section className="py-14 md:py-20 px-4 md:px-8 bg-[#050507] border-y border-white/5" data-id="element-119" data-mp="3gzm50882hjnb">
        <div className="max-w-2xl mx-auto" data-id="element-120" data-mp="3lwcl2vjvgf8q">
          <motion.h2 initial={{
          opacity: 0,
          y: 12
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-2xl font-black text-white text-center mb-8" style={{
          fontFamily: "'Poppins',sans-serif"
        }} data-id="element-121" data-mp="3lv1gwumil5h3">
            Preguntas <span className="text-[#25D366]" data-id="element-122" data-mp="2y7jjojkhdf6q">frecuentes</span>
          </motion.h2>
          <FAQ data-id="element-123" data-mp="32y9qm18k1q3j" />
        </div>
      </section>

      {/* ══ CIERRE FINAL ═════════════════════════════════════════════ */}
      <section className="py-16 md:py-24 px-4 md:px-8 relative overflow-hidden" data-id="element-124" data-mp="2oaeyalyglnqz">
        <div className="blob-1 absolute inset-0 w-full h-full bg-[#25D366]/5 -z-10 pointer-events-none" style={{
        borderRadius: 0,
        animation: 'none'
      }} data-id="element-125" data-mp="2t86y025cie5q" />
        <div className="blob-2 absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-[#25D366]/8 -z-10 pointer-events-none" data-id="element-126" data-mp="2f9qrocbeapo4" />

        <motion.div initial={{
        opacity: 0,
        y: 24
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="max-w-lg mx-auto text-center" data-id="element-127" data-mp="2zqbg1jhzmr1a">
          <div className="inline-flex items-center gap-2 bg-[#25D366]/12 border border-[#25D366]/25 rounded-full px-4 py-2 mb-6" data-id="element-128" data-mp="2up48nljrbtv5">
            <ShieldCheck className="w-4 h-4 text-[#25D366]" data-id="element-129" data-mp="2ktanxevvwu2a" />
            <span className="text-[#25D366] text-sm font-bold" data-id="element-130" data-mp="2kuvgx02jw4y1">Soporte por WhatsApp incluido</span>
          </div>

          <h2 className="text-3xl md:text-4xl font-black text-white mb-4 leading-tight" style={{
          fontFamily: "'Poppins',sans-serif"
        }} data-id="element-131" data-mp="2c0h3z45ht6i3">
            Toma el control de tu negocio{' '}
            <span className="text-transparent bg-clip-text gradient-animate" style={{
            backgroundImage: 'linear-gradient(90deg,#25D366,#34d399,#22d3ee,#25D366)',
            backgroundSize: '200%'
          }} data-id="element-132" data-mp="26z9y5amwuhe2">
              hoy mismo.
            </span>
          </h2>

          <p className="text-gray-400 text-base mb-8" data-id="element-133" data-mp="1t2o1u42p10lr">
            $19 a Tasa BCV del día · Pago único · Sin mensualidades
          </p>

          <div className="flex flex-col gap-3" data-id="element-134" data-mp="8ln708y914di">
            <BtnWhatsApp label="Comprar el Masterkit por $19 a Tasa BCV →" data-id="element-135" data-mp="cke78oy0h24u" />
            <BtnCheckout data-id="element-136" data-mp="hkifjrormqjz" />
          </div>

          <div className="mt-6 flex flex-col gap-1" data-id="element-137" data-mp="hljz6kjukldb">
            <p className="text-gray-600 text-xs" data-id="element-138" data-mp="mj3gjtbude9h">Pago Móvil · Binance Pay · Tarjeta Internacional</p>
            <p className="text-amber-500/50 text-xs" data-id="element-139" data-mp="3l191xh4nng7u">Precio en bolívares calculado a Tasa BCV oficial del día del pago</p>
          </div>
        </motion.div>
      </section>

      {/* ══ FOOTER ════════════════════════════════════════════════════ */}
      <footer className="py-6 px-4 border-t border-white/5 bg-[#030305]" data-id="element-140" data-mp="3uxkhcfzjkm6i">
        <p className="text-center text-gray-600 text-xs" data-id="element-141" data-mp="3lj11cuyvs1j">
          © {new Date().getFullYear()} Digital Solvex · Barquisimeto, Venezuela ·{' '}
          <a href="https://toolkit.digitalsolvex.shop" className="hover:text-gray-400 transition-colors" data-id="element-142" data-mp="328dex5wyvcfz">
            Ver todos los planes
          </a>
        </p>
      </footer>

      {/* ══ BOTÓN WA FLOTANTE ════════════════════════════════════════ */}
      <motion.a href={WA_URL} target="_blank" rel="noopener noreferrer" whileHover={{
      scale: 1.12
    }} whileTap={{
      scale: 0.95
    }} onClick={() => (window as any).fbq?.('track', 'Contact')} className="fixed bottom-5 right-5 z-[9999] group" data-id="element-143" data-mp="2lxp1t9rr7pmi">
        <span className="absolute right-14 top-1/2 -translate-y-1/2 bg-white text-black text-xs font-bold
          py-1.5 px-3 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity shadow-lg whitespace-nowrap pointer-events-none" data-id="element-144" data-mp="1y8w0exsd4pki">
          ¿Dudas? Escríbeme 👋
        </span>
        <div className="relative w-[52px] h-[52px] bg-[#25D366] rounded-full flex items-center justify-center
          shadow-[0_0_24px_rgba(37,211,102,.5)]" data-id="element-145" data-mp="1t9jf4hh53qk1">
          <div className="absolute inset-0 bg-[#25D366] rounded-full animate-ping opacity-25" data-id="element-146" data-mp="1fcec6qcq37t0" />
          <svg viewBox="0 0 32 32" className="w-7 h-7 text-white relative z-10" xmlns="http://www.w3.org/2000/svg" data-id="element-147" data-mp="1k8lca2iftp31">
            <path d="M16.002 2.186c-7.615 0-13.791 6.175-13.791 13.791 0 2.433.633 4.811 1.834 6.895l-1.956 7.143 7.309-1.917c2.046 1.096 4.35 1.674 6.605 1.674 7.612 0 13.788-6.175 13.788-13.791 0-7.615-6.176-13.795-13.789-13.795zm0 25.143c-2.062 0-4.088-.553-5.857-1.602l-.42-.249-4.355 1.142 1.161-4.246-.273-.435c-1.153-1.834-1.761-3.953-1.761-6.143 0-6.309 5.132-11.442 11.443-11.442s11.44 5.133 11.44 11.442c-.001 6.31-5.133 11.442-11.442 11.442zm6.276-8.561c-.344-.173-2.035-1.005-2.351-1.12-.315-.115-.545-.173-.775.173-.229.345-.888 1.12-1.089 1.35-.201.229-.402.258-.745.086-2.147-.946-3.69-2.095-5.123-4.577-.184-.319.18-.302.516-.973.086-.173.043-.328-.043-.501-.086-.173-.775-1.868-1.062-2.558-.279-.672-.562-.581-.775-.592-.2-.009-.43-.009-.659-.009-.229 0-.602.086-.918.432-.315.345-1.205 1.178-1.205 2.872 0 1.693 1.233 3.328 1.405 3.558.173.229 2.425 3.7 5.874 5.19 2.508 1.083 3.42.923 4.022.836.699-.101 2.035-.833 2.322-1.638.286-.805.286-1.495.201-1.638-.087-.146-.317-.233-.66-.406z" fill="currentColor" data-id="element-148" data-mp="10pflwyplqjfv" />
          </svg>
        </div>
      </motion.a>
    </div>;
}